// ============================== GLOBAL IMPORTS ==============================
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Molstar Viewer and UI components
import { MolStarWrapper } from './molstarViewer';
// React and OpenDialog Chatbot
import React from 'react';
import * as ReactDOM from 'react-dom/client';
import ChatbotOD from './chatbotOD';
// Components of MexaForm
import { initReceptorForm } from './components/receptorForm';
// initBindingSiteForm removed - binding site logic moved to receptorForm.ts
import { initPeptideForm, updatePeptideDetailsGlobal } from './components/peptideForm';
// forceShowPeptideInputStep removed - no longer needed with new tab structure
import { initDockingForm } from './components/dockingForm';
import { initRefreshAllStatusesButton } from './components/statusManager';
// Authentication components
import { initLoginForm, initRegisterForm, initPasswordChangeForm, initPasswordResetForm, initPasswordResetConfirmForm, initUserProfile, initAuthGuards, onAuthStateChange } from './components/authForm';
// Stylesheet
import './style.css';
// Chatbot React root reference so we can unmount properly before re-mounting (avoids duplicate messages on account switch)
let chatbotReactRoot = null;
// ============================== CATALOG FUNCTIONS ==============================
// Dynamic lists - will be detected from available images
let NCAAS_LIST = [];
let CANONICAL_AAS_LIST = [];
let HEAD_TO_TAIL_PEPTIDES = [];
let DISULFIDE_BRIDGES_PEPTIDES = [];
// Function to detect all available non-canonical amino acids from image files
function detectAvailableNCAAs() {
    return __awaiter(this, void 0, void 0, function* () {
        const availableNCAAs = [];
        // Since we can't directly list files in frontend, we'll use a more dynamic approach
        // We'll try to detect files by checking common patterns and known files
        // This is a more realistic approach for frontend-only detection
        // Known NCAA files that should exist (you can add/remove from this list)
        const knownNCAAs = [
            '0AR', '48V', '4FB', '55I', '6CV', '6CW', 'ABA', 'ABU', 'ALa', 'ALC', 'API', 'ARg', 'ASh', 'ASn', 'ASp', 'BAL', 'BMT', 'BOH', 'BPH', 'CIR', 'Cpa', 'CYa', 'CYm', 'CYs', 'DA2', 'DAB', 'DHA',
            'ESA', 'F2F', 'GAl', 'GB1', 'GB3', 'GL2', 'GL3', 'GL4', 'GL5', 'GLh', 'GLn', 'GLu', 'GP2',
            'HId', 'HIe', 'HIp', 'HRG', 'HSE', 'HyP', 'IML', 'LEI', 'LMM', 'LPG', 'LYs',
            'LYZ', 'M07', 'M12', 'M34', 'M51', 'M70', 'MEA', 'MLE', 'MME', 'MN1', 'MRZ', 'MTO', 'MTR', 'MVA', 'MYN',
            'NAL', 'NLe', 'NLY', 'NMM', 'NVa', 'O11', 'OA1', 'OCS', 'ORN', 'PH8', 'Psa', 'PTR', 'RZ4', 'SAR', 'SDM',
            'SEr', 'Tba', 'THr', 'Tr4', 'Tr6', 'Tr7', 'TRp', 'TYC', 'TYr', 'UAL', 'UN1', 'V7T', 'V8N', 'VR0', 'XW1'
        ];
        // Check each known NCAA to see if the file actually exists
        const checkPromises = knownNCAAs.map((code) => __awaiter(this, void 0, void 0, function* () {
            try {
                const img = new Image();
                return new Promise((resolve) => {
                    img.onload = () => {
                        console.log(`✅ Found NCAA: ${code}`);
                        resolve(code);
                    };
                    img.onerror = () => {
                        console.log(`❌ Missing NCAA: ${code}`);
                        resolve(null);
                    };
                    img.src = `/assets/NCAAs_pics/resp-${code}.png`;
                });
            }
            catch (error) {
                console.log(`❌ Error checking NCAA: ${code}`, error);
                return null;
            }
        }));
        const results = yield Promise.all(checkPromises);
        const foundCodes = results.filter((code) => code !== null);
        console.log(`🔍 NCAA Detection Complete: Found ${foundCodes.length} available NCAAs:`, foundCodes);
        return foundCodes;
    });
}
// Dynamic lists populated from detected images
// Function to detect available cyclic peptides from image files
function detectAvailableCyclicPeptides() {
    return __awaiter(this, void 0, void 0, function* () {
        const headToTail = [];
        const disulfideBridges = [];
        // Function to check and parse peptide files from a directory
        function checkPeptideFiles(directory, fileList) {
            return __awaiter(this, void 0, void 0, function* () {
                const peptides = [];
                for (const filename of fileList) {
                    try {
                        const img = new Image();
                        const imagePath = `assets/${directory}/${filename}`;
                        const exists = yield new Promise((resolve) => {
                            img.onload = () => resolve(true);
                            img.onerror = () => resolve(false);
                            img.src = imagePath;
                        });
                        if (exists) {
                            // Extract name and sequence from filename (format: name_sequence.png)
                            const nameWithoutExt = filename.replace('.png', '');
                            const parts = nameWithoutExt.split('_');
                            const name = parts[0];
                            const sequence = parts.slice(1).join('_');
                            peptides.push({
                                name: name,
                                sequence: sequence,
                                imagePath: imagePath
                            });
                        }
                    }
                    catch (error) {
                        console.warn(`Could not check for ${filename} image:`, error);
                    }
                }
                return peptides;
            });
        }
        // Check head-to-tail peptides directory
        const headToTailFiles = [
            'BQ-123_dTRP1b-dASP-PRO-dVAL-LEU1b.png',
            'Cyclosporine_dALA1b-MLE-MLE-MVA-BMT-ABA-SAR-MLE-VAL-MLE-ALA1b.png',
            'Murepavadin_dPRO1b-SER-ALA-DAB-DAB-TRP-DAB-dDAB-ORN-DAB-ILE-TRP-THR-PRO1b.png'
        ];
        const detectedHeadToTail = yield checkPeptideFiles('head-to-tail_pics', headToTailFiles);
        headToTail.push(...detectedHeadToTail);
        // Check disulfide-bridges peptides directory
        const disulfideBridgesFiles = [
            'Setmelanotide_ACE-ARG-CYS1s-dALA-HIS-dPHE-ARG-TRP-CYS1s-NH2.png',
            'Oxytocin_CYS1s-TYR-ILE-GLN-ASN-CYS1s-PRO-LEU-GLY.png',
            'Vasopressin_CYS1s-TYR-PHE-GLN-ASN-CYS1s-PRO-ARG-LYS.png'
        ];
        const detectedDisulfideBridges = yield checkPeptideFiles('disulfide-bridges_pics', disulfideBridgesFiles);
        disulfideBridges.push(...detectedDisulfideBridges);
        return { headToTail, disulfideBridges };
    });
}
// Function to detect all available canonical amino acids from image files
function detectAvailableCanonicalAAs() {
    return __awaiter(this, void 0, void 0, function* () {
        const availableAAs = [];
        // Get all actual files from the CAA_pics directory
        const caaFiles = [
            { code: 'ALA', name: 'Alanine' },
            { code: 'ARG', name: 'Arginine' },
            { code: 'ASN', name: 'Asparagine' },
            { code: 'ASP', name: 'Aspartic acid' },
            { code: 'CYS', name: 'Cysteine' },
            { code: 'GLN', name: 'Glutamine' },
            { code: 'GLU', name: 'Glutamic acid' },
            { code: 'GLY', name: 'Glycine' },
            { code: 'HIE', name: 'Histidine' }, // Using HIE as it's the file that exists
            { code: 'ILE', name: 'Isoleucine' },
            { code: 'LEU', name: 'Leucine' },
            { code: 'LYS', name: 'Lysine' },
            { code: 'MET', name: 'Methionine' },
            { code: 'PHE', name: 'Phenylalanine' },
            { code: 'PRO', name: 'Proline' },
            { code: 'SER', name: 'Serine' },
            { code: 'THR', name: 'Threonine' },
            { code: 'TRP', name: 'Tryptophan' },
            { code: 'TYR', name: 'Tyrosine' },
            { code: 'VAL', name: 'Valine' }
        ];
        // Check each amino acid file exists
        const checkPromises = caaFiles.map((aa) => __awaiter(this, void 0, void 0, function* () {
            try {
                const img = new Image();
                return new Promise((resolve) => {
                    img.onload = () => {
                        console.log(`✅ Found CAA: ${aa.code}`);
                        resolve(aa);
                    };
                    img.onerror = () => {
                        console.log(`❌ Missing CAA: ${aa.code}`);
                        resolve(null);
                    };
                    img.src = `/assets/CAA_pics/${aa.code}.png`;
                });
            }
            catch (error) {
                console.log(`❌ Error checking CAA: ${aa.code}`, error);
                return null;
            }
        }));
        const results = yield Promise.all(checkPromises);
        const foundAAs = results.filter((aa) => aa !== null);
        console.log(`🔍 CAA Detection Complete: Found ${foundAAs.length} available CAAs:`, foundAAs.map(aa => aa.code));
        return foundAAs;
    });
}
function createNCAAsGrid() {
    return __awaiter(this, void 0, void 0, function* () {
        // Detect available NCAAs if not already done
        if (NCAAS_LIST.length === 0) {
            console.log('🔍 Detecting available NCAAs...');
            NCAAS_LIST = yield detectAvailableNCAAs();
            console.log(`✅ Found ${NCAAS_LIST.length} available NCAAs:`, NCAAS_LIST);
        }
        const gridItems = NCAAS_LIST.map(ncaa => `
    <div class="ncaa-item" data-ncaa="${ncaa}" onclick="selectAminoAcid('${ncaa}')">
      <div class="ncaa-image-container">
        <img src="/assets/NCAAs_pics/resp-${ncaa}.png" alt="${ncaa}" class="ncaa-image" onerror="this.style.display='none'" />
      </div>
      <div class="ncaa-name">${ncaa}</div>
    </div>
  `).join('');
        return `
    <div class="ncaas-grid-container">
      <div class="ncaas-grid">
        ${gridItems}
      </div>
    </div>
  `;
    });
}
function createHeadToTailGrid() {
    return __awaiter(this, void 0, void 0, function* () {
        // Detect available head-to-tail peptides if not already done
        if (HEAD_TO_TAIL_PEPTIDES.length === 0) {
            console.log('🔍 Detecting available head-to-tail peptides...');
            const detectedPeptides = yield detectAvailableCyclicPeptides();
            HEAD_TO_TAIL_PEPTIDES = detectedPeptides.headToTail;
            console.log(`✅ Found ${HEAD_TO_TAIL_PEPTIDES.length} head-to-tail peptides:`, HEAD_TO_TAIL_PEPTIDES.map(p => p.name));
        }
        const gridItems = HEAD_TO_TAIL_PEPTIDES.map((peptide, index) => {
            // Use detected image path or placeholder
            const imageSrc = peptide.imagePath || '/assets/peptides/placeholder.png';
            return `
      <div class="peptide-item" data-sequence="${peptide.sequence}" onclick="selectPeptideSequence('${peptide.sequence}')">
        <div class="peptide-image-container">
          <img src="${imageSrc}" alt="${peptide.name}" class="peptide-image" onerror="this.style.display='none'" />
        </div>
        <div class="peptide-info">
          <div class="peptide-name">${peptide.name}</div>
          <div class="peptide-sequence">${peptide.sequence}</div>
        </div>
      </div>
    `;
        }).join('');
        // Always add "More Coming Soon" item for head-to-tail
        const comingSoonItems = `
    <div class="peptide-item coming-soon">
      <div class="peptide-image-container"></div>
      <div class="peptide-info">
        <div class="peptide-name">More Coming Soon</div>
        <div class="peptide-sequence">Additional peptides will be added</div>
      </div>
    </div>
  `;
        return `
    <div class="peptides-grid-container">
      <div class="peptides-grid">
        ${gridItems}
        ${comingSoonItems}
      </div>
    </div>
  `;
    });
}
function createDisulfideBridgesGrid() {
    return __awaiter(this, void 0, void 0, function* () {
        // Detect available disulfide bridges peptides if not already done
        if (DISULFIDE_BRIDGES_PEPTIDES.length === 0) {
            console.log('🔍 Detecting available disulfide bridges peptides...');
            const detectedPeptides = yield detectAvailableCyclicPeptides();
            DISULFIDE_BRIDGES_PEPTIDES = detectedPeptides.disulfideBridges;
            console.log(`✅ Found ${DISULFIDE_BRIDGES_PEPTIDES.length} disulfide bridges peptides:`, DISULFIDE_BRIDGES_PEPTIDES.map(p => p.name));
        }
        const gridItems = DISULFIDE_BRIDGES_PEPTIDES.map((peptide, index) => {
            // Use detected image path or placeholder
            const imageSrc = peptide.imagePath || '/assets/peptides/placeholder.png';
            return `
      <div class="peptide-item" data-sequence="${peptide.sequence}" onclick="selectPeptideSequence('${peptide.sequence}')">
        <div class="peptide-image-container">
          <img src="${imageSrc}" alt="${peptide.name}" class="peptide-image" onerror="this.style.display='none'" />
        </div>
        <div class="peptide-info">
          <div class="peptide-name">${peptide.name}</div>
          <div class="peptide-sequence">${peptide.sequence}</div>
        </div>
      </div>
    `;
        }).join('');
        // Always add "More Coming Soon" item for disulfide bridges
        const comingSoonItems = `
    <div class="peptide-item coming-soon">
      <div class="peptide-image-container"></div>
      <div class="peptide-info">
        <div class="peptide-name">More Coming Soon</div>
        <div class="peptide-sequence">Additional peptides will be added</div>
      </div>
    </div>
  `;
        return `
    <div class="peptides-grid-container">
      <div class="peptides-grid">
        ${gridItems}
        ${comingSoonItems}
      </div>
    </div>
  `;
    });
}
function createCanonicalAAsGrid() {
    return __awaiter(this, void 0, void 0, function* () {
        // Detect available canonical amino acids if not already done
        if (CANONICAL_AAS_LIST.length === 0) {
            console.log('🔍 Detecting available canonical amino acids...');
            CANONICAL_AAS_LIST = yield detectAvailableCanonicalAAs();
            console.log(`✅ Found ${CANONICAL_AAS_LIST.length} available canonical AAs:`, CANONICAL_AAS_LIST.map(aa => aa.code));
        }
        const gridItems = CANONICAL_AAS_LIST.map(aa => {
            return `
      <div class="ncaa-item" data-ncaa="${aa.code}" onclick="selectAminoAcid('${aa.code}')">
        <div class="ncaa-image-container">
          <img src="/assets/CAA_pics/${aa.code}.png" alt="${aa.code}" class="ncaa-image" onerror="this.style.display='none'" />
        </div>
        <div class="ncaa-name">${aa.code}</div>
      </div>
    `;
        }).join('');
        return `
    <div class="ncaas-grid-container">
      <div class="ncaas-grid">
        ${gridItems}
      </div>
    </div>
  `;
    });
}
// Function to handle amino acid selection
function selectAminoAcid(code) {
    console.log(`Amino acid selected: ${code}`);
    // Check if Input Peptide tab is open
    const peptideTab = document.getElementById('peptide-tab');
    const peptideTabButton = document.querySelector('[data-target="peptide-tab"]');
    // If tab is not open, open it
    if (peptideTab && peptideTab.classList.contains('hidden')) {
        console.log('Ouverture de l\'onglet Input Peptide');
        // Deactivate all other tabs
        const allTabButtons = document.querySelectorAll('.tab-button');
        const allTabContents = document.querySelectorAll('.tab-content');
        allTabButtons.forEach(btn => btn.classList.remove('active'));
        allTabContents.forEach(tab => tab.classList.add('hidden'));
        // Activer l'onglet Input Peptide
        if (peptideTabButton) {
            peptideTabButton.classList.add('active');
        }
        peptideTab.classList.remove('hidden');
    }
    // Get peptide input
    const peptideInput = document.getElementById('peptide-input');
    if (!peptideInput) {
        console.error('Input peptide non trouvé');
        return;
    }
    // Get cursor position
    const cursorPosition = peptideInput.selectionStart;
    const currentValue = peptideInput.value;
    // Determine text to insert
    let textToInsert = code;
    // If it's the beginning of the field or if the field is empty, no dash before
    if (cursorPosition === 0 || currentValue.length === 0) {
        textToInsert = code;
    }
    // Add a dash before if necessary (if there is already text and we are not at the beginning)
    else if (cursorPosition > 0 && currentValue.charAt(cursorPosition - 1) !== '-' && currentValue.charAt(cursorPosition - 1) !== ' ') {
        textToInsert = '-' + code;
    }
    // Add a dash after if necessary (if there is text after and it's not a dash)
    if (cursorPosition < currentValue.length && currentValue.charAt(cursorPosition) !== '-' && currentValue.charAt(cursorPosition) !== ' ') {
        textToInsert = textToInsert + '-';
    }
    // Insert text at cursor position
    const newValue = currentValue.substring(0, cursorPosition) + textToInsert + currentValue.substring(cursorPosition);
    peptideInput.value = newValue;
    // Reposition cursor after inserted text
    const newCursorPosition = cursorPosition + textToInsert.length;
    peptideInput.setSelectionRange(newCursorPosition, newCursorPosition);
    // Give focus to input to maintain cursor position
    peptideInput.focus();
    console.log(`Code ${code} added at position ${cursorPosition}, new cursor at ${newCursorPosition}`);
}
// Function to handle full peptide sequence selection
function selectPeptideSequence(sequence) {
    console.log(`Peptide sequence selected: ${sequence}`);
    // Check if Input Peptide tab is open
    const peptideTab = document.getElementById('peptide-tab');
    const peptideTabButton = document.querySelector('[data-target="peptide-tab"]');
    // If tab is not open, open it
    if (peptideTab && peptideTab.classList.contains('hidden')) {
        console.log('Opening Input Peptide tab for full sequence');
        // Deactivate all other tabs
        const allTabButtons = document.querySelectorAll('.tab-button');
        const allTabContents = document.querySelectorAll('.tab-content');
        allTabButtons.forEach(btn => btn.classList.remove('active'));
        allTabContents.forEach(tab => tab.classList.add('hidden'));
        // Activer l'onglet Input Peptide
        if (peptideTabButton) {
            peptideTabButton.classList.add('active');
        }
        peptideTab.classList.remove('hidden');
    }
    // Get peptide input
    const peptideInput = document.getElementById('peptide-input');
    if (!peptideInput) {
        console.error('Input peptide non trouvé');
        return;
    }
    // Get cursor position
    const cursorPosition = peptideInput.selectionStart;
    const currentValue = peptideInput.value;
    // Determine text to insert
    let textToInsert = sequence;
    // If it's the beginning of the field or if the field is empty, no dash before
    if (cursorPosition === 0 || currentValue.length === 0) {
        textToInsert = sequence;
    }
    // Add a dash before if necessary (if there is already text and we are not at the beginning)
    else if (cursorPosition > 0 && currentValue.charAt(cursorPosition - 1) !== '-' && currentValue.charAt(cursorPosition - 1) !== ' ') {
        textToInsert = '-' + sequence;
    }
    // Add a dash after if necessary (if there is text after and it's not a dash)
    if (cursorPosition < currentValue.length && currentValue.charAt(cursorPosition) !== '-' && currentValue.charAt(cursorPosition) !== ' ') {
        textToInsert = textToInsert + '-';
    }
    // Insert text at cursor position
    const newValue = currentValue.substring(0, cursorPosition) + textToInsert + currentValue.substring(cursorPosition);
    peptideInput.value = newValue;
    // Reposition cursor after inserted text
    const newCursorPosition = cursorPosition + textToInsert.length;
    peptideInput.setSelectionRange(newCursorPosition, newCursorPosition);
    // Give focus to input to maintain cursor position
    peptideInput.focus();
    console.log(`Sequence ${sequence} added at position ${cursorPosition}, new cursor at ${newCursorPosition}`);
}
// Initialize and expose Mol* viewer globally
const wrapper = new MolStarWrapper();
wrapper.init('viewer-container');
// Expose molstarWrapper on window for binding site events
window.molstarWrapper = wrapper;
window.loadPdbInMolstar = (pdbId, pdbFilePath) => {
    console.log(`Loading PDB: ${pdbId} from ${pdbFilePath} in Mol*`);
    return wrapper.loadStructureWithStyle(pdbId, pdbFilePath);
};
// Assign selection functions to window
window.selectAminoAcid = selectAminoAcid;
window.selectPeptideSequence = selectPeptideSequence;
// Thin global to call the strongly-typed viewer method
window.loadDockingPosesInMolstar = (pdbId, posePaths) => {
    wrapper.loadDockingPosesAsModels(pdbId, posePaths);
};
// Expose Mol* removal functions globally
window.removeFromMolstar = (pdbId) => {
    console.log(`Removing PDB: ${pdbId} from Mol*`);
    return wrapper.removeStructure(pdbId);
};
window.clearMolstar = () => {
    console.log('Clearing all structures from Mol*');
    return wrapper.clearAllStructures();
};
// TEMPORARY: Expose event capture function for debugging
window.captureEvent = wrapper.captureEventObject;
// Expose updatePeptideDetailsGlobal function globally
window.updatePeptideDetailsGlobal = updatePeptideDetailsGlobal;
// ============================== CONFIRMATION MODAL ==============================
/**
 * Shows a confirmation modal and returns a promise that resolves to true if confirmed, false if cancelled
 */
export function showToast(message, type = 'info', duration = 4000) {
    // Try to get container, create it if it doesn't exist
    let container = document.getElementById('toast-container');
    if (!container) {
        container = document.createElement('div');
        container.id = 'toast-container';
        container.className = 'toast-container';
        document.body.appendChild(container);
    }
    // Remove all existing toasts IMMEDIATELY (no animation delay)
    const existingToasts = container.querySelectorAll('.toast');
    existingToasts.forEach((existingToast) => {
        if (existingToast.parentNode) {
            existingToast.parentNode.removeChild(existingToast);
        }
    });
    // Use requestAnimationFrame to ensure DOM is updated before adding new toast
    requestAnimationFrame(() => {
        const toast = document.createElement('div');
        toast.className = `toast toast-${type}`;
        const icon = type === 'success' ? '✅' : type === 'error' ? '❌' : type === 'warning' ? '⚠️' : 'ℹ️';
        toast.innerHTML = `
      <span class="toast-icon">${icon}</span>
      <span class="toast-message">${message}</span>
    `;
        container.appendChild(toast);
        // Trigger animation immediately (toast appears directly at top)
        requestAnimationFrame(() => {
            toast.classList.add('show');
        });
        // Auto-remove after duration
        setTimeout(() => {
            toast.classList.remove('show');
            setTimeout(() => {
                if (toast.parentNode) {
                    toast.parentNode.removeChild(toast);
                }
            }, 300);
        }, duration);
    });
}
export function showConfirmationModal(title, message, confirmText = 'Delete', cancelText = 'Cancel') {
    return new Promise((resolve) => {
        const modal = document.getElementById('confirm-modal');
        const titleEl = document.getElementById('confirm-modal-title');
        const messageEl = document.getElementById('confirm-modal-message');
        const confirmBtn = document.getElementById('confirm-modal-confirm');
        const cancelBtn = document.getElementById('confirm-modal-cancel');
        if (!modal || !titleEl || !messageEl || !confirmBtn || !cancelBtn) {
            console.error('Confirmation modal elements not found');
            resolve(false);
            return;
        }
        // Set content
        titleEl.textContent = title;
        messageEl.textContent = message;
        confirmBtn.textContent = confirmText;
        cancelBtn.textContent = cancelText;
        // Show modal
        modal.classList.remove('hidden');
        // Clean up function
        const cleanup = () => {
            modal.classList.add('hidden');
            confirmBtn.onclick = null;
            cancelBtn.onclick = null;
            modal.onclick = null;
            document.removeEventListener('keydown', handleEscape);
        };
        // Handle escape key
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                cleanup();
                resolve(false);
            }
        };
        // Confirm button
        confirmBtn.onclick = () => {
            console.log('[DEBUG] Confirmation modal: Confirm button clicked');
            cleanup();
            console.log('[DEBUG] Confirmation modal: Resolving with true');
            resolve(true);
        };
        // Cancel button
        cancelBtn.onclick = () => {
            cleanup();
            resolve(false);
        };
        // Close on overlay click
        modal.onclick = (e) => {
            if (e.target === modal) {
                cleanup();
                resolve(false);
            }
        };
        // Listen for escape key
        document.addEventListener('keydown', handleEscape);
    });
}
// Expose globally for use in components
window.showConfirmationModal = showConfirmationModal;
window.showToast = showToast;
// Helper to load prepared receptor after successful preparation
function loadPreparedReceptorInMolstar(pdbID, pdbPath) {
    var _a;
    console.log(`🧬 Loading receptor ${pdbID} from ${pdbPath}`);
    (_a = window.loadPdbInMolstar) === null || _a === void 0 ? void 0 : _a.call(window, pdbID, pdbPath);
}
// ============================== CHATBOT SETUP ==============================
// Chatbot will be initialized after user authentication
// ============================== PDB Catalog Toggle ==============================
// Function to calculate modal position based on actual info panel width
function applyModalPositioning(modal) {
    if (!modal)
        return;
    // Get the actual info panel element to calculate its real width
    const infoPanel = document.getElementById('info-panel');
    if (!infoPanel) {
        console.warn('Info panel not found, using default positioning');
        return;
    }
    // Get the actual computed width of the info panel
    const infoPanelRect = infoPanel.getBoundingClientRect();
    const infoPanelWidth = infoPanelRect.width;
    // Position modal right after info panel
    modal.style.left = `${infoPanelWidth}px`;
    // Position modal with small margins top and bottom
    modal.style.top = '70px'; // Start with small top margin
    // Calculate remaining width for modal
    const remainingWidth = window.innerWidth - infoPanelWidth;
    const modalContent = modal.querySelector('.modal-content');
    if (modalContent) {
        modalContent.style.width = `${remainingWidth - 40}px`; // 40px for padding
        // Adjust height with small margins top and bottom
        modalContent.style.height = 'calc(100vh - 100px)'; // 100px = 70px top + 30px bottom margin
    }
    console.log(`🔍 [DEBUG] Info panel width: ${infoPanelWidth}px, Modal positioned at: ${infoPanelWidth}px, Modal width: ${remainingWidth - 40}px`);
}
document.addEventListener('DOMContentLoaded', () => {
    const btnCyclic = document.getElementById('btn-cyclic');
    const btnAminoAcids = document.getElementById('btn-amino-acids');
    // Cyclic peptides modal elements
    const cyclicPeptidesModal = document.getElementById('cyclic-peptides-modal');
    const cyclicPeptidesModalBody = document.getElementById('cyclic-peptides-modal-body');
    const cyclicPeptidesCloseBtn = document.getElementById('cyclic-peptides-modal-close');
    const tabHeadToTail = document.getElementById('tab-head-to-tail');
    const tabDisulfideBridges = document.getElementById('tab-disulfide-bridges');
    // Amino acids modal elements
    const aminoAcidsModal = document.getElementById('amino-acids-modal');
    const aminoAcidsModalBody = document.getElementById('amino-acids-modal-body');
    const aminoAcidsCloseBtn = document.getElementById('amino-acids-modal-close');
    const tabCanonical = document.getElementById('tab-canonical');
    const tabNoncanonical = document.getElementById('tab-noncanonical');
    console.log('🔍 Elements found:', {
        btnCyclic: !!btnCyclic,
        btnAminoAcids: !!btnAminoAcids,
        cyclicPeptidesModal: !!cyclicPeptidesModal,
        aminoAcidsModal: !!aminoAcidsModal,
        cyclicPeptidesModalBody: !!cyclicPeptidesModalBody,
        aminoAcidsModalBody: !!aminoAcidsModalBody,
        tabHeadToTail: !!tabHeadToTail,
        tabDisulfideBridges: !!tabDisulfideBridges,
        tabCanonical: !!tabCanonical,
        tabNoncanonical: !!tabNoncanonical
    });
    // Cyclic peptides modal
    if (btnCyclic) {
        console.log('✅ Cyclic peptides button found, adding event listener');
        btnCyclic.addEventListener('click', () => {
            console.log('🔥 Cyclic peptides button clicked');
            // Close amino acids modal if open and remove active state
            if (aminoAcidsModal && !aminoAcidsModal.classList.contains('hidden')) {
                aminoAcidsModal.classList.add('hidden');
                btnAminoAcids === null || btnAminoAcids === void 0 ? void 0 : btnAminoAcids.classList.remove('active');
            }
            if (cyclicPeptidesModal) {
                console.log('🔥 Removing hidden class from cyclic peptides modal');
                cyclicPeptidesModal.classList.remove('hidden');
                btnCyclic.classList.add('active'); // Add active state
                // Apply positioning after modal is shown to ensure info panel is rendered
                setTimeout(() => {
                    applyModalPositioning(cyclicPeptidesModal);
                    loadHeadToTailTab();
                }, 50);
            }
            else {
                console.error('❌ Cyclic peptides modal not found');
            }
        });
    }
    else {
        console.error('❌ Cyclic peptides button not found');
    }
    // Amino acids modal
    if (btnAminoAcids) {
        console.log('✅ Amino acids button found, adding event listener');
        btnAminoAcids.addEventListener('click', () => {
            console.log('🔥 Amino acids button clicked');
            // Close cyclic peptides modal if open and remove active state
            if (cyclicPeptidesModal && !cyclicPeptidesModal.classList.contains('hidden')) {
                cyclicPeptidesModal.classList.add('hidden');
                btnCyclic === null || btnCyclic === void 0 ? void 0 : btnCyclic.classList.remove('active');
            }
            if (aminoAcidsModal) {
                console.log('🔥 Removing hidden class from amino acids modal');
                aminoAcidsModal.classList.remove('hidden');
                btnAminoAcids.classList.add('active'); // Add active state
                // Apply positioning after modal is shown to ensure info panel is rendered
                setTimeout(() => {
                    applyModalPositioning(aminoAcidsModal);
                    loadCanonicalTab();
                }, 50);
            }
            else {
                console.error('❌ Amino acids modal not found');
            }
        });
    }
    else {
        console.error('❌ Amino acids button not found');
    }
    // Functions for cyclic peptides tabs
    function loadHeadToTailTab() {
        return __awaiter(this, void 0, void 0, function* () {
            console.log('🔥 Loading head-to-tail tab');
            if (tabHeadToTail && tabDisulfideBridges && cyclicPeptidesModalBody) {
                tabHeadToTail.classList.add('active');
                tabDisulfideBridges.classList.remove('active');
                cyclicPeptidesModalBody.innerHTML = yield createHeadToTailGrid();
                console.log('✅ Head-to-tail tab loaded');
            }
            else {
                console.error('❌ Missing elements for head-to-tail tab');
            }
        });
    }
    function loadDisulfideBridgesTab() {
        return __awaiter(this, void 0, void 0, function* () {
            console.log('🔥 Loading disulfide bridges tab');
            if (tabHeadToTail && tabDisulfideBridges && cyclicPeptidesModalBody) {
                tabDisulfideBridges.classList.add('active');
                tabHeadToTail.classList.remove('active');
                cyclicPeptidesModalBody.innerHTML = yield createDisulfideBridgesGrid();
                console.log('✅ Disulfide bridges tab loaded');
            }
            else {
                console.error('❌ Missing elements for disulfide bridges tab');
            }
        });
    }
    // Functions for amino acids tabs
    function loadCanonicalTab() {
        return __awaiter(this, void 0, void 0, function* () {
            console.log('🔥 Loading canonical tab');
            if (tabCanonical && tabNoncanonical && aminoAcidsModalBody) {
                tabCanonical.classList.add('active');
                tabNoncanonical.classList.remove('active');
                aminoAcidsModalBody.innerHTML = yield createCanonicalAAsGrid();
                console.log('✅ Canonical tab loaded');
            }
            else {
                console.error('❌ Missing elements for canonical tab');
            }
        });
    }
    function loadNoncanonicalTab() {
        return __awaiter(this, void 0, void 0, function* () {
            console.log('🔥 Loading non-canonical tab');
            if (tabCanonical && tabNoncanonical && aminoAcidsModalBody) {
                tabNoncanonical.classList.add('active');
                tabCanonical.classList.remove('active');
                aminoAcidsModalBody.innerHTML = yield createNCAAsGrid();
                console.log('✅ Non-canonical tab loaded');
            }
            else {
                console.error('❌ Missing elements for non-canonical tab');
            }
        });
    }
    // Tab switching for cyclic peptides
    if (tabHeadToTail) {
        tabHeadToTail.addEventListener('click', loadHeadToTailTab);
    }
    if (tabDisulfideBridges) {
        tabDisulfideBridges.addEventListener('click', loadDisulfideBridgesTab);
    }
    // Tab switching for amino acids
    if (tabCanonical) {
        tabCanonical.addEventListener('click', loadCanonicalTab);
    }
    if (tabNoncanonical) {
        tabNoncanonical.addEventListener('click', loadNoncanonicalTab);
    }
    // Close buttons
    if (cyclicPeptidesCloseBtn) {
        cyclicPeptidesCloseBtn.addEventListener('click', () => {
            cyclicPeptidesModal.classList.add('hidden');
            btnCyclic === null || btnCyclic === void 0 ? void 0 : btnCyclic.classList.remove('active'); // Remove active state when closing
        });
    }
    if (aminoAcidsCloseBtn) {
        aminoAcidsCloseBtn.addEventListener('click', () => {
            aminoAcidsModal.classList.add('hidden');
            btnAminoAcids === null || btnAminoAcids === void 0 ? void 0 : btnAminoAcids.classList.remove('active'); // Remove active state when closing
        });
    }
    // Close modals when clicking outside
    if (cyclicPeptidesModal) {
        cyclicPeptidesModal.addEventListener('click', (e) => {
            if (e.target === cyclicPeptidesModal) {
                cyclicPeptidesModal.classList.add('hidden');
                btnCyclic === null || btnCyclic === void 0 ? void 0 : btnCyclic.classList.remove('active'); // Remove active state when closing
            }
        });
    }
    if (aminoAcidsModal) {
        aminoAcidsModal.addEventListener('click', (e) => {
            if (e.target === aminoAcidsModal) {
                aminoAcidsModal.classList.add('hidden');
                btnAminoAcids === null || btnAminoAcids === void 0 ? void 0 : btnAminoAcids.classList.remove('active'); // Remove active state when closing
            }
        });
    }
    // Recalculate modal positioning on window resize
    window.addEventListener('resize', () => {
        // Add small delay to ensure layout is updated
        setTimeout(() => {
            if (cyclicPeptidesModal && !cyclicPeptidesModal.classList.contains('hidden')) {
                applyModalPositioning(cyclicPeptidesModal);
            }
            if (aminoAcidsModal && !aminoAcidsModal.classList.contains('hidden')) {
                applyModalPositioning(aminoAcidsModal);
            }
        }, 100);
    });
});
// ============================== TABS & PANEL HANDLING ==============================
document.addEventListener('DOMContentLoaded', () => {
    const tabButtons = document.querySelectorAll('.tab-button');
    const tabContents = document.querySelectorAll('.tab-content');
    tabButtons.forEach(button => {
        button.addEventListener('click', () => {
            var _a, _b;
            // Prevent clicking on disabled tabs
            if (button.classList.contains('disabled')) {
                return;
            }
            tabButtons.forEach(btn => btn.classList.remove('active'));
            tabContents.forEach(tab => tab.classList.add('hidden'));
            button.classList.add('active');
            const targetId = button.getAttribute('data-target');
            const targetTab = document.getElementById(targetId || '');
            if (targetTab)
                targetTab.classList.remove('hidden');
            // If user clicks on Peptide tab, load selected peptide in viewer
            if (targetId === 'peptide-tab') {
                // Hide binding site and screening interfaces when switching to peptide tab
                const bindingSiteInterface = document.getElementById('binding-site-interface');
                const screeningInterface = document.getElementById('screening-interface');
                if (bindingSiteInterface)
                    bindingSiteInterface.style.display = 'none';
                if (screeningInterface)
                    screeningInterface.style.display = 'none';
                // Show the receptor list (in case it was hidden)
                const receptorList = document.getElementById('wizard-step-1');
                if (receptorList) {
                    receptorList.style.display = 'block';
                }
                if (typeof window.currentPeptideID === 'number') {
                    // Get current user from localStorage
                    const currentUser = localStorage.getItem('currentUser');
                    if (currentUser) {
                        const userData = JSON.parse(currentUser);
                        const username = userData.username;
                        const sessionID = 1; // Use session 1 for Cognito users
                        const peptidePath = `/frontend/userSessions/dataBase/peptide-${String(window.currentPeptideID).padStart(5, '0')}/reprPeptide.pdb`;
                        (_a = window.loadPdbInMolstar) === null || _a === void 0 ? void 0 : _a.call(window, `peptide-${window.currentPeptideID}`, peptidePath);
                    }
                }
            }
            // If user clicks on Receptor tab, and a receptor is selected, show it in the viewer
            if (targetId === 'receptor-tab') {
                // Hide binding site and screening interfaces when switching to receptor tab
                const bindingSiteInterface = document.getElementById('binding-site-interface');
                const screeningInterface = document.getElementById('screening-interface');
                if (bindingSiteInterface)
                    bindingSiteInterface.style.display = 'none';
                if (screeningInterface)
                    screeningInterface.style.display = 'none';
                // Show the receptor list
                const receptorList = document.getElementById('wizard-step-1');
                if (receptorList) {
                    receptorList.style.display = 'block';
                }
                if (typeof window.currentReceptorID === 'number' && window.currentPdbID) {
                    // Get current user from localStorage
                    const currentUser = localStorage.getItem('currentUser');
                    if (currentUser) {
                        const userData = JSON.parse(currentUser);
                        const username = userData.username;
                        const sessionID = 1; // Use session 1 for Cognito users
                        // Load receptor without binding sites (interface is now hidden)
                        const receptorPath = `/frontend/userSessions/dataBase/receptor-${String(window.currentReceptorID).padStart(5, '0')}/rec.pdb`;
                        (_b = window.loadPdbInMolstar) === null || _b === void 0 ? void 0 : _b.call(window, window.currentPdbID, receptorPath);
                    }
                }
            }
            // If user clicks on Docking tab, load selected docking result in viewer
            if (targetId === 'docking-tab') {
                // Hide binding site and screening interfaces when switching to docking tab
                const bindingSiteInterface = document.getElementById('binding-site-interface');
                const screeningInterface = document.getElementById('screening-interface');
                if (bindingSiteInterface)
                    bindingSiteInterface.style.display = 'none';
                if (screeningInterface)
                    screeningInterface.style.display = 'none';
                // Show the receptor list (in case it was hidden)
                const receptorList = document.getElementById('wizard-step-1');
                if (receptorList) {
                    receptorList.style.display = 'block';
                }
                const dockingContainer = document.getElementById('docking-list-container');
                const selectedDocking = dockingContainer === null || dockingContainer === void 0 ? void 0 : dockingContainer.querySelector('.docking-item.selected');
                if (selectedDocking) {
                    selectedDocking.click();
                }
            }
        });
    });
});
let currentUserSession = null;
// Strict session validation - ensures data belongs to current user
function validateUserSession(expectedUserId) {
    if (!currentUserSession || !currentUserSession.isActive) {
        console.error('🔒 Security: No active user session');
        return false;
    }
    if (currentUserSession.userId !== expectedUserId) {
        console.error(`🔒 Security: User session mismatch. Expected: ${expectedUserId}, Current: ${currentUserSession.userId}`);
        return false;
    }
    // Session remains valid until user logs out or browser closes
    return true;
}
// Create frontend session state (NOT a database session)
function createFrontendSessionState(userId, sessionId) {
    // Invalidate any existing frontend session state first
    invalidateUserSession();
    // Create local frontend state object for validation
    currentUserSession = {
        userId, // ← For frontend validation
        sessionId, // ← Backend session ID (already exists in DB)
        isActive: true // ← Frontend state flag
    };
    console.log(`🔒 Security: Frontend session state created for user: ${userId}, using backend session: ${sessionId}`);
}
// Invalidate current session
function invalidateUserSession() {
    if (currentUserSession) {
        console.log(`🔒 Security: Invalidating session for user: ${currentUserSession.userId}`);
        currentUserSession.isActive = false;
    }
    currentUserSession = null;
}
// Clear ALL user data and reset session
function clearAllUserData() {
    console.log('🔒 Security: Clearing all user data and invalidating session');
    // Invalidate session first
    invalidateUserSession();
    // Clear ALL global state variables (receptor, peptide, binding site, screening)
    window.currentPdbID = null;
    window.currentReceptorID = null;
    window.currentBindingSiteID = null;
    window.currentPeptideID = undefined;
    window.currentCustomBindingSiteID = undefined;
    window.nBindingSites = undefined;
    window.receptorWithPocketsPath = undefined;
    window.bindingSiteSubmitted = undefined;
    // Clear all lists (receptors, peptides, dockings, screenings)
    const lists = ['receptor-list-container', 'peptide-list-container', 'docking-list-container', 'screening-list-container'];
    lists.forEach(id => {
        const element = document.getElementById(id);
        if (element) {
            if (id === 'docking-list-container') {
                element.innerHTML = '<p class="no-dockings">No dockings performed yet</p>';
            }
            else if (id === 'screening-list-container') {
                element.innerHTML = '<div class="no-items screening-no-items">No screenings yet</div>';
            }
            else {
                element.innerHTML = '';
            }
            if (id === 'receptor-list-container') {
                element.style.display = 'none';
            }
        }
    });
    // Reset UI elements (tabs, buttons, wizard steps)
    resetTabsToDefault();
    resetButtonStates();
    // Clear Mol* viewer
    if (window.clearMolstar) {
        window.clearMolstar();
    }
    // Clear chat history
    const chatHistory = document.getElementById('chat-history');
    if (chatHistory) {
        chatHistory.innerHTML = '';
        console.log('💬 Chat history cleared in clearAllUserData');
    }
    // Reset component-specific global states
    resetComponentStates();
    // Force garbage collection hint
    if (window.gc) {
        window.gc();
    }
}
// Reset component-specific global states
function resetComponentStates() {
    // Reset docking form states
    if (typeof window.dockingList !== 'undefined') {
        window.dockingList = [];
    }
    if (typeof window.nextDockingID !== 'undefined') {
        window.nextDockingID = 1;
    }
    if (typeof window.isDeletingDocking !== 'undefined') {
        window.isDeletingDocking = false;
    }
    // Reset receptor form states
    if (typeof window.isDeletingReceptor !== 'undefined') {
        window.isDeletingReceptor = false;
    }
    if (typeof window.deletionQueue !== 'undefined') {
        window.deletionQueue = [];
    }
    // Clear per-receptor binding site state so it is not shared between users
    if (typeof window.bindingSiteByReceptor !== 'undefined') {
        window.bindingSiteByReceptor = {};
    }
    // Reset button states and UI elements
    resetButtonStates();
    // Clear all text/number inputs (including receptor PDB ID and upload name)
    const inputs = document.querySelectorAll('input[type="text"], input[type="number"]');
    inputs.forEach(input => { input.value = ''; });
    // Reset upload file input and its label
    const uploadFileInput = document.getElementById('receptor-upload-file');
    if (uploadFileInput)
        uploadFileInput.value = '';
    const fileLabel = document.getElementById('receptor-file-label');
    if (fileLabel) {
        fileLabel.textContent = 'Browse PDB file';
        fileLabel.classList.remove('has-file');
    }
    // Hide duplicate-name hint and re-enable upload button state
    const uploadNameHint = document.getElementById('receptor-upload-name-hint');
    if (uploadNameHint)
        uploadNameHint.style.display = 'none';
    const submitUploadBtn = document.getElementById('submit-receptor-upload');
    if (submitUploadBtn)
        submitUploadBtn.disabled = true;
    const submitReceptorBtn = document.getElementById('submit-receptor');
    if (submitReceptorBtn)
        submitReceptorBtn.disabled = true;
    // Reset any form validation states
    const errorElements = document.querySelectorAll('.error-message, .success-message');
    errorElements.forEach(el => el.textContent = '');
}
// Force reset tabs to default state (Receptor tab) 
function resetTabsToDefault() {
    console.log('🔄 Force resetting tabs to default state');
    // Reset all tabs
    const tabButtons = document.querySelectorAll('.tab-button');
    const tabContents = document.querySelectorAll('.tab-content');
    tabButtons.forEach(btn => btn.classList.remove('active'));
    tabContents.forEach(tab => tab.classList.add('hidden'));
    // Activate first tab (Receptor)
    const firstTab = tabButtons[0];
    const firstContent = document.getElementById((firstTab === null || firstTab === void 0 ? void 0 : firstTab.getAttribute('data-target')) || '');
    if (firstTab) {
        firstTab.classList.add('active');
        console.log('✅ Activated first tab:', firstTab.getAttribute('data-target'));
    }
    if (firstContent) {
        firstContent.classList.remove('hidden');
        console.log('✅ Showed first content:', firstContent.id);
    }
    // Reset binding site form specifically (this was the main issue)
    const bindingSiteForm = document.getElementById('binding-site-form');
    if (bindingSiteForm) {
        bindingSiteForm.style.display = 'none';
    }
    // Close screening and binding site sub-views so new user never sees previous user's data
    const screeningInterface = document.getElementById('screening-interface');
    const bindingSiteInterface = document.getElementById('binding-site-interface');
    const receptorList = document.getElementById('wizard-step-1');
    const formTitle = document.querySelector('.form-title');
    // Hide sub-interfaces (screening, binding site)
    if (screeningInterface)
        screeningInterface.style.display = 'none';
    if (bindingSiteInterface)
        bindingSiteInterface.style.display = 'none';
    // Show main receptor list and main form title
    if (receptorList)
        receptorList.style.display = 'block';
    if (formTitle)
        formTitle.style.display = 'block';
    // Clear screening list content (stale data from previous user)
    const screeningListContainer = document.getElementById('screening-list-container');
    if (screeningListContainer) {
        screeningListContainer.innerHTML = '<div class="no-items screening-no-items">No screenings yet</div>';
    }
    // Clear custom binding sites list (stale data from previous user)
    const customBindingSitesContainer = document.getElementById('custom-binding-sites-list-container');
    if (customBindingSitesContainer) {
        customBindingSitesContainer.innerHTML = '<div class="no-items" style="padding: 20px; text-align: center; color: #999;">No custom binding sites yet. Create one using "Multiple Residues" mode.</div>';
    }
    // Reset any selected binding sites and screening items
    const selectedBindingSites = document.querySelectorAll('.binding-site-item.selected, .screening-item.selected');
    selectedBindingSites.forEach(item => item.classList.remove('selected'));
}
// Reset all button states and UI elements
function resetButtonStates() {
    // Reset step 2 button (binding site step)
    const step2Btn = document.getElementById('step-2-btn');
    if (step2Btn) {
        step2Btn.classList.add('disabled');
        step2Btn.disabled = true;
        step2Btn.title = 'Select a binding site first';
    }
    // Reset peptide step 2 button (docking results)
    const peptideStep2Btn = document.getElementById('peptide-step-2-btn');
    if (peptideStep2Btn) {
        peptideStep2Btn.classList.add('disabled');
        peptideStep2Btn.title = 'Docking results will appear here';
    }
    // Reset all delete buttons states
    const deleteButtons = document.querySelectorAll('.btn-delete');
    deleteButtons.forEach(btn => {
        btn.disabled = false;
        btn.style.display = '';
        btn.style.visibility = 'visible';
        btn.textContent = '×';
    });
    // Reset all refresh buttons states
    const refreshButtons = document.querySelectorAll('.btn-check');
    refreshButtons.forEach(btn => {
        btn.style.display = '';
        btn.style.visibility = 'visible';
    });
    // Reset all status elements
    const statusElements = document.querySelectorAll('.peptide-status');
    statusElements.forEach(status => {
        status.textContent = '';
        status.className = 'peptide-status';
    });
    // Reset all clickable states
    const clickableElements = document.querySelectorAll('.clickable, .not-clickable');
    clickableElements.forEach(el => {
        el.classList.remove('clickable', 'not-clickable');
    });
    // Reset all selected states
    const selectedElements = document.querySelectorAll('.selected');
    selectedElements.forEach(el => {
        el.classList.remove('selected');
    });
    // Reset progress bars
    const progressBars = document.querySelectorAll('.progress-bar');
    progressBars.forEach(bar => {
        bar.style.width = '0%';
    });
    // Reset status message elements
    const statusMessages = document.querySelectorAll('[id^="status-message-"]');
    statusMessages.forEach(msg => {
        msg.textContent = '';
        msg.style.display = 'none';
    });
}
// Centralized secure data loading function
function loadUserDataSecurely() {
    return __awaiter(this, void 0, void 0, function* () {
        // Validate session before any data loading
        const currentUser = localStorage.getItem('currentUser');
        if (!currentUser) {
            console.error('🔒 Security: No authenticated user found, aborting data load');
            return;
        }
        const userData = JSON.parse(currentUser);
        const userId = userData.username;
        // Validate session ownership
        if (!validateUserSession(userId)) {
            console.error('🔒 Security: User session validation failed, aborting data load');
            return;
        }
        console.log(`🔒 Security: Loading data for validated user: ${userId}`);
        // Load all data in parallel
        try {
            const { loadSessionDockings } = yield import('./components/dockingForm');
            const { loadSessionReceptors } = yield import('./components/receptorForm');
            const { loadSessionPeptides } = yield import('./components/peptideForm');
            yield Promise.all([
                loadSessionDockings(),
                loadSessionReceptors(),
                loadSessionPeptides()
            ]);
            // updateBindingSiteButtonState is already called in loadSessionDockings() after loading completes
            // No need for additional setTimeout since Promise.all() now properly waits for all loaders
        }
        catch (error) {
            console.error('Error loading user data:', error);
        }
    });
}
// ============================== WIZARD STEP: PREPARE RECEPTOR ==============================
document.addEventListener('DOMContentLoaded', () => {
    // Initialize authentication first
    initAuthGuards();
    initLoginForm();
    initRegisterForm();
    initPasswordChangeForm();
    initPasswordResetForm();
    initPasswordResetConfirmForm();
    initUserProfile();
    // Initialize global state variables
    window.currentPdbID = null;
    window.currentReceptorID = null;
    window.currentBindingSiteID = null;
    // Track current peptide selection for tab-driven visualization
    window.currentPeptideID = undefined;
    // Initialize forms only when user is authenticated
    onAuthStateChange((user, authenticated) => __awaiter(void 0, void 0, void 0, function* () {
        if (authenticated && (user === null || user === void 0 ? void 0 : user.username)) {
            console.log('🔔 User authenticated - setting up strict session for:', user.username);
            // Clear any existing data first
            clearAllUserData();
            // Get the actual session ID from backend before creating session
            try {
                const { getActiveSession } = yield import('./backendClient');
                const sessionResult = yield getActiveSession();
                const sessionId = sessionResult.success ? sessionResult.sessionID : 1;
                // Create frontend session state with backend session ID
                createFrontendSessionState(user.username, sessionId);
            }
            catch (error) {
                console.error('Failed to get session ID, using default:', error);
                createFrontendSessionState(user.username, 1);
            }
            // Initialize all forms
            initDockingForm();
            initReceptorForm();
            initPeptideForm();
            // Initialize refresh all statuses button
            initRefreshAllStatusesButton();
            // Initialize chatbot AFTER authentication (so it has access to tokens)
            const chatbotRoot = document.getElementById('chatbot-root');
            if (chatbotRoot) {
                // Unmount previous chatbot instance properly so cleanup runs (avoids duplicate messages on account switch)
                if (chatbotReactRoot) {
                    chatbotReactRoot.unmount();
                    chatbotReactRoot = null;
                    console.log('🤖 Previous chatbot instance unmounted');
                }
                // Clear chat history for new user
                const chatHistory = document.getElementById('chat-history');
                if (chatHistory) {
                    chatHistory.innerHTML = '';
                    console.log('💬 Chat history cleared for new user');
                }
                // Create new chatbot instance for this user
                chatbotReactRoot = ReactDOM.createRoot(chatbotRoot);
                chatbotReactRoot.render(React.createElement(ChatbotOD, null));
                console.log('🤖 New chatbot initialized for user:', user.username);
            }
            // Load data ONLY after session is created and forms are initialized
            setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
                yield loadUserDataSecurely();
            }), 100); // Minimal delay just to ensure DOM is ready
        }
        else {
            console.log('🔔 User not authenticated - clearing all data and invalidating session');
            clearAllUserData();
            // Remove chatbot when user logs out (proper unmount so cleanup runs)
            if (chatbotReactRoot) {
                chatbotReactRoot.unmount();
                chatbotReactRoot = null;
                console.log('🤖 Chatbot removed due to logout');
            }
            // Clear chat history when user logs out
            const chatHistory = document.getElementById('chat-history');
            if (chatHistory) {
                chatHistory.innerHTML = '';
                console.log('💬 Chat history cleared due to logout');
            }
        }
    }));
});
// ============================== WIZARD STEP: SELECT BINDING SITE ==============================
document.addEventListener('DOMContentLoaded', () => {
    // initBindingSiteForm removed - binding site logic moved to receptorForm.ts
});
// ============================== LOGOUT EVENT LISTENER ==============================
// Listen for logout events to force UI reset
document.addEventListener('logout', () => {
    console.log('🔔 Logout event detected - forcing UI reset');
    clearAllUserData();
});
// ============================== PEPTIDE INPUT HANDLING ==============================
// (Moved to authentication callback above)
// ============================== WIZARD STEP HANDLING ==============================
// Wizard step handling removed - no longer needed
// Focus styles handled by CSS only
